import { sessionStopRecording } from '@/lib/analytics/scribe/session';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  activeRecordingTransportAtom,
  chronicleAwaitingFinalNoteAtom,
  chronicleBackgroundSessionAtom,
  chronicleChunkLiveStoppingSessionsAtom,
  chronicleDeviceStateAtom,
  chronicleLiveOfflineAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleRecordingEndReasonAtom,
  chronicleRecordingOriginAtom,
  chronicleSessionIdAtom,
  chronicleSupportsChunkLiveAtom,
  type ChronicleDeviceState,
  type ChronicleRecordingEndReason,
  type ChronicleRecordingOrigin,
} from '@/atoms/chronicle';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import {
  isGenerateConsultNoteFromDocumentTemplateEnabled,
  useIsReleaseRemoteDirtyStopRecoveryEnabled,
  useIsReleaseRemoteSyncAsyncWebEnabled,
} from '@/features/flags';
import { IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED } from '@/features/flags/legacy-flags';
import useMutateCreateDocumentFromNote from '@/hooks/mutation/useMutateCreateDocumentFromNote';
import {
  invalidateInterruptedSessions,
  refreshInterruptedSessionsFromRust,
  setInterruptedSessionCacheMembership,
} from '@/hooks/query/desktop/chronicle/useQueryInterruptedSessions';
import { setIsPollingChunks } from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { GET_TRANSCRIPT_BY_SESSION_ID } from '@/hooks/query/useQueryTranscriptBySessionId';
import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { type NoteGenerateTrigger } from '@/lib/telemetry/noteGenerateTracking';
import {
  finishRecordingControlAttempt,
  setRecordingControlTransport,
  type RecordingControlTracking,
} from '@/lib/telemetry/recordingControlTracking';
import type { HardwareRecordingStopReason } from '@/models/analytics/features/chronicle';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { store, type Dispatch } from '@/store';
import { toast } from '@heidi/design-system/toast';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import {
  beginArtifactGeneration,
  completeArtifactGeneration,
  getArtifactGenerationSnapshot,
  resetArtifactGeneration,
  useQuerySessionById,
} from '@heidi/session/data';

import { chronicleBridge } from '@/tauri/chronicle';
import {
  chronicleSessionBridge,
  type StopSessionResult,
} from '@/tauri/chronicle/session';
import {
  trackHardwareRecordingStop,
  trackHardwareRecordingStopFailed,
} from '@/utils/analytics/track-hardware-events';
import {
  mapRouteToPipeline,
  mapStopOutcomeForDeviceEvent,
  trackRemoteRecordingStopped,
} from '@/utils/analytics/track-remote-device-events';
import { boundedErrorName } from '@/utils/logger/bounded-error-name';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { isTranscribeLikeMode } from '@/utils/transcribeMode';
import { recordingLifecycle } from '@heidi/plugin-core';
import { useTranslations } from '@heidi/runtime/i18n';
import { sanitizeError } from '@heidi/runtime/logger';
import {
  GenerationMethod,
  RecordingStatus,
  TemplateCategory,
  type TranscribeMode,
} from '@heidi/schema/ml-scribe';
import { useAutoSetSessionDefaultTemplate } from './useAutoSetSessionDefaultTemplate';

// ---------------------------------------------------------------------------
// Post-recording generation helper (extracted to reduce cognitive complexity)
// ---------------------------------------------------------------------------

/**
 * Mirrors the branching in `useMutateStopRecording` for note/document generation.
 *
 * Uses `sessionData.transcribe_mode` to choose flow and `template` (fetched via
 * `useQueryTemplateById(consult_note.template_id, consult_note.generation_method)`)
 * to determine if it's a document/form template.
 */
function triggerPostRecordingGeneration({
  sessionId,
  sessionData,
  template,
  createEmptyDocumentFromNote,
  dispatch,
}: {
  sessionId: string;
  sessionData:
    | {
        transcribe_mode?: TranscribeMode | null;
        consult_note?: {
          template_id?: string | null;
          generation_method?: GenerationMethod | null;
        };
      }
    | undefined;
  template: { template_category?: TemplateCategory; id?: string } | undefined;
  createEmptyDocumentFromNote: (input: {
    generationMethod: GenerationMethod;
    templateCategory?: TemplateCategory;
    templateId?: string;
    trigger: NoteGenerateTrigger;
  }) => void;
  dispatch: Dispatch;
}) {
  const shouldUseDocumentFlow =
    isTranscribeLikeMode(sessionData?.transcribe_mode) &&
    ((!isGenerateConsultNoteFromDocumentTemplateEnabled() &&
      template?.template_category === TemplateCategory.DOCUMENT_TEMPLATE) ||
      (IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED() &&
        template?.template_category === TemplateCategory.FORM_TEMPLATE));

  if (shouldUseDocumentFlow && template) {
    createEmptyDocumentFromNote({
      trigger: 'autonomous',
      generationMethod: GenerationMethod.TEMPLATE,
      templateCategory: template.template_category,
      templateId: template.id,
    });
    completeArtifactGeneration({ kind: 'note', sessionId });
  } else {
    // Handles TRANSCRIBE (non-document templates) and DICTATE modes.
    // _actionPostRecording reads session from query cache to determine
    // generationMethod, templateId, dictationCleanupMode, etc.
    dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
  }
}

/**
 * `stop_reason` for a stop's `hardware_recording_stop`. An explicit end reason
 * wins. The device's sticky `lastStopReason` survives disconnect and scan, so
 * it attributes this stop only alongside an end reason that the tagged
 * `MCU&STO` itself produces — never bare `isInterrupted`, or an unrelated later
 * interrupted stop would inherit it. Pairing `poweroff_batlow` with
 * `normal_stop` is safe because a recording beginning clears the reason in
 * Rust: a later recording's own stop arrives in a `connected` payload that
 * already carries no reason. The state channel keeps only its latest value, so
 * this does not depend on every payload reaching the frontend.
 */
function resolveHardwareStopReason({
  endReason,
  lastStopReason,
  isInterrupted,
}: {
  endReason: ChronicleRecordingEndReason | null;
  lastStopReason: ChronicleDeviceState['lastStopReason'];
  isInterrupted: boolean | undefined;
}): HardwareRecordingStopReason {
  if (
    endReason === 'max_duration' ||
    (lastStopReason === 'max_duration' && endReason === 'ble_disconnect')
  ) {
    return 'max_duration';
  }
  if (
    endReason === 'poweroff_batlow' ||
    (lastStopReason === 'poweroff_batlow' &&
      (endReason === 'normal_stop' || endReason === 'ble_disconnect'))
  ) {
    return 'low_battery';
  }
  return isInterrupted ? 'ble_disconnect' : 'normal';
}

// Shared across hook instances — stop fires from both useRecording (desktop)
// and useChronicleSessionControl (device echo), so a per-instance ref can't dedupe.
/** PHI-safe log fields for a caught error: its bounded name plus any HTTP status/code. */
const safeErrorFields = (error: unknown) => ({
  name: boundedErrorName(error),
  ...sanitizeError(error),
});

const stoppingSessionIds = new Set<string>();

// The recording_stop attempt waiting on each session's in-flight stop. A click
// can join a stop already running, device-origin or not, so the running stop
// closes the attempt rather than the call that carried the click.
const pendingStopAttempt = new Map<string, RecordingControlTracking>();

type ChronicleStopAttemptOutcome = {
  metadata?: { recordingDurationS: number };
} & ({ failed: false } | { failed: true; error: unknown });

// Outcomes of in-flight stops that already passed the device stop and flush,
// so a desktop click joining late reports the stop it joined.
const capturedStopOutcomes = new Map<string, ChronicleStopAttemptOutcome>();

const finishStopAttempt = (
  tracking: RecordingControlTracking | undefined,
  outcome: ChronicleStopAttemptOutcome
): void => {
  if (outcome.failed) {
    finishRecordingControlAttempt(tracking, {
      outcome: 'failed',
      error: outcome.error,
      metadata: outcome.metadata,
    });
  } else {
    finishRecordingControlAttempt(tracking, { metadata: outcome.metadata });
  }
};

// The invoke bridge sets no deadline of its own — a hung IPC must not wedge
// the cleanup steps that follow the awaited interrupted-set refresh.
const INTERRUPTED_SET_REFRESH_TIMEOUT_MS = 5_000;

// Ceiling on the widget suppression a chunk-live stop holds. Comfortably over
// Rust's 5-minute STOP_DRAIN_DEADLINE, because a stop IPC that never settles
// runs no `finally` — without this the sync widget would stay hidden for the
// rest of the session, and the reset only fires on logout.
const CHUNK_LIVE_STOP_SUPPRESSION_CEILING_MS = 7 * 60_000;

/**
 * What a `stopChronicleRecording` call actually did (APP-7870): `'stopped'`
 * owned the full flow (and its post-stop UX, e.g. navigation); `'duplicate'`
 * no-oped against an in-flight stop; `'no_session'` = best-effort device stop.
 */
export type ChronicleStopOutcome = 'stopped' | 'duplicate' | 'no_session';

/**
 * Core hook for stopping a Chronicle recording session.
 *
 * Reuses the same post-recording flow as `useMutateStopRecording.ts`:
 * - **Document flow** (TRANSCRIBE + DOCUMENT/FORM template):
 *   `createEmptyDocumentFromNote` → `_streamResponse` (SSE) → `generateDocumentContent`
 * - **Non-document / Dictate flow**:
 *   `_actionPostRecording` → `_streamResponse` (SSE)
 *
 * Key differences from `useMutateStopRecording`:
 * - **No frontend backup/chunk handling**: Backup and chunks are uploaded
 *   entirely in Rust by `src-tauri/session/online_session.rs`.
 * - **No `tauriRecordModel` tracking**: Upload pipeline runs in Rust with
 *   serial FIFO processing. `waitForChunksToFinishUploading` resolves
 *   immediately (vacuous truth — `tauriRecordModel.chunks.blobs` is empty).
 * - **Authoritative duration**: Timer is synced to `durationMs` returned by
 *   `chronicle_session_stop()`, not from the frontend timer state.
 * - **No direct device commands**: Rust session layer handles device stop
 *   internally — no separate `chronicleBridge.stopRecording()` call.
 *
 * Three-phase flow (each phase runs regardless of prior failures):
 *
 * **Phase 1 — Stop session** (try/catch):
 *   1. Set `streamingStatus = UPLOADING` (optimistic UI → shows loading overlay)
 *   2. `setIsPollingChunks` (signals live transcript polling)
 *   3. `chronicle_session_stop()` → stops device + flushes pipeline + uploads
 *      in a single Rust call (device stop is idempotent for device-initiated)
 *
 * **Phase 2 — Post-recording generation** (always runs, separate try/catch):
 *   5. Document flow → `createEmptyDocumentFromNote` → `_streamResponse` (SSE)
 *      Non-document/Dictate → `_actionPostRecording` → `_streamResponse` (SSE)
 *      Runs even after pipeline flush failure (backend may have partial audio).
 *
 * **Phase 3 — Cleanup** (always runs):
 *   6. Sync timer to authoritative `durationMs`
 *   7. Set `recording_status = IDLE` in cache + PATCH backend
 *   8. Invalidate query caches (transcript, session)
 *   9. Clear Jotai atoms (sessionId, origin)
 *   10. Restart desktop idle stream (mic release)
 *   11. Track analytics
 *
 * Called from both:
 * - Desktop UI stop click (via `useRecording` → origin: 'desktop_app')
 * - Device-initiated stop (via `useChronicleSessionControl` → origin: 'remote_device')
 */
export function useChronicleStopRecording() {
  const { t } = useTranslations();
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const setChronicleSessionId = useSetAtom(chronicleSessionIdAtom);
  const setRecordingOrigin = useSetAtom(chronicleRecordingOriginAtom);
  const setActiveRecordingTransport = useSetAtom(activeRecordingTransportAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const setBackgroundSession = useSetAtom(chronicleBackgroundSessionAtom);
  const setReconnectedPendingEnd = useSetAtom(chronicleReconnectedPendingEndAtom);
  const setLiveOffline = useSetAtom(chronicleLiveOfflineAtom);
  const setAwaitingFinalNote = useSetAtom(chronicleAwaitingFinalNoteAtom);
  const isRemoteSyncAsyncEnabled = useIsReleaseRemoteSyncAsyncWebEnabled();
  const isDirtyStopRecoveryEnabled = useIsReleaseRemoteDirtyStopRecoveryEnabled();
  // Read the reconnected-pending-end atom fresh at stop time (see Phase 1),
  // from the same store the setters above write to.
  const jotaiStore = useStore();

  const { ensureTemplateSelected } = useAutoSetSessionDefaultTemplate();

  // --- Session & template data for post-recording flow branching ---
  // (mirrors useMutateStopRecording's session/template queries)
  const { data: sessionData } = useQuerySessionById({
    sessionId: chronicleSessionId ?? '',
    select: (data) => {
      if (!data) return undefined;
      return {
        transcribe_mode: data.transcribe_mode,
        consult_note: data.consult_note,
      };
    },
  });

  const { data: template } = useQueryTemplateById(
    sessionData?.consult_note?.template_id,
    sessionData?.consult_note?.generation_method
  );

  const { mutate: createEmptyDocumentFromNote } = useMutateCreateDocumentFromNote({
    sessionId: chronicleSessionId ?? '',
  });

  const stopChronicleRecording = useCallback(
    async (args: {
      origin: ChronicleRecordingOrigin;
      /** True when BLE disconnected mid-recording. Preserves sync_tracking record for recovery. */
      isInterrupted?: boolean;
      /** The attempt a desktop press opened; a device-side stop carries none. */
      tracking?: RecordingControlTracking;
    }): Promise<ChronicleStopOutcome> => {
      setRecordingControlTransport(args.tracking, 'remote_device');
      if (!chronicleSessionId) {
        // The session atom was cleared (e.g. navigated away, or a reconnect that
        // reset local state) but the device may still be recording. Send a
        // best-effort device-level APP&STO so the stream isn't orphaned (APP-7571):
        // the firmware keeps recording until an explicit stop, so a skipped stop
        // here is exactly what leaves the device holding a stream that rejects the
        // next start. We can't run the full upload/generation flow without a
        // session id, but we can still close the device stream.
        logger.warn(
          '[useChronicleStopRecording] No active Chronicle session id; sending best-effort device stop to avoid orphaning the device stream'
        );
        let deviceStopSucceeded = true;
        try {
          await chronicleBridge.stopRecording();
        } catch (error) {
          deviceStopSucceeded = false;
          logger.warn('[useChronicleStopRecording] Best-effort device stop failed', {
            error: safeErrorFields(error),
          });
        }
        // A hold can be armed with no bound session (BLE drop between the
        // device `recording` event and `startChronicleRecording` resolving).
        // Release it here too — otherwise manual End leaves the Remote card
        // + Infinity disconnect toast up, and 120s later the hold deadline
        // misclassifies this End as an interrupted BLE drop.
        const liveOffline = jotaiStore.get(chronicleLiveOfflineAtom);
        setLiveOffline(null);
        // Keyed window release only when the hold itself owned the window;
        // with no chronicleSessionId we cannot claim a foreign session's.
        if (
          liveOffline?.sessionId != null &&
          jotaiStore.get(chronicleReconnectedPendingEndAtom) === liveOffline.sessionId
        ) {
          setReconnectedPendingEnd(null);
        }
        // No bound session: the click's intent still needs a terminal attempt,
        // and the device stop is the capture stop it asked for.
        finishStopAttempt(
          args.tracking,
          deviceStopSucceeded
            ? { failed: false }
            : {
                failed: true,
                error: new DesktopCaptureError(
                  DesktopCaptureFailure.RECORDER_STOP_FAILED,
                  'No active Chronicle session and best-effort device stop failed'
                ),
              }
        );
        return 'no_session';
      }
      const sessionId = chronicleSessionId;

      if (stoppingSessionIds.has(sessionId)) {
        logger.warn(
          '[useChronicleStopRecording] Already stopping this session, joining the in-flight stop'
        );
        // This click takes the in-flight stop's outcome. One landing after its
        // device stop and flush reports that outcome now; an earlier one joins it.
        const capturedOutcome = capturedStopOutcomes.get(sessionId);
        if (capturedOutcome) {
          finishStopAttempt(args.tracking, capturedOutcome);
        } else if (args.tracking) {
          // Opening this handle superseded any earlier one, so keep only it.
          pendingStopAttempt.set(sessionId, args.tracking);
        }
        return 'duplicate';
      }
      stoppingSessionIds.add(sessionId);
      if (args.tracking) pendingStopAttempt.set(sessionId, args.tracking);

      try {
        let uploadResult: StopSessionResult | null = null;
        let stopFlushError: unknown;
        let preserveDeviceFileForRecovery = false;
        // Read BEFORE the optimistic block below releases the hold: a stop
        // landing during a live-offline hold ends a session whose device link
        // dropped mid-recording. The audio since the drop exists only on the
        // device, so the stop must classify as interrupted — a clean stop's
        // cleanup would discard the recovery path for it.
        const isInterrupted = jotaiStore.get(chronicleLiveOfflineAtom)
          ? true
          : args.isInterrupted;

        // --- Optimistic UI: immediately transition away from recording controls ---
        // 1. Stop the timer right away so the user sees recording controls disappear.
        //    We preserve the current count; the authoritative duration from Rust
        //    will overwrite it in Phase 3 once the IPC returns.
        const currentSeconds = store.getState().timerModel.seconds;
        dispatch.timerModel.onStopWithDuration(currentSeconds);

        // Clear the nav-guard atom in the same synchronous block that stops
        // the timer — it must not wait on the blocking stop IPC below, or
        // cross-session navigation stays blocked for the whole upload drain.
        setActiveSessionId(null);
        // Release a live-offline hold before the blocking IPC, not after it:
        // this stop ends the session the hold was waiting on, and a hold left
        // set while the stop is in flight lets the hold deadline fire mid-stop
        // and misclassify a manual End as an interrupted BLE drop.
        setLiveOffline(null);

        // 2. Set recording_status → IDLE + streamingStatus → UPLOADING in one
        //    cache update.  This hides stop/pause buttons (isRecording becomes
        //    false) and shows the "Processing…" overlay simultaneously.
        setIsPollingChunks(sessionId);
        const sessionBeforeUpload = queryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId },
        ]);
        beginArtifactGeneration({
          key: { kind: 'note', sessionId },
          outgoingContent: sessionBeforeUpload?.consult_note?.result ?? null,
          phase: 'uploading',
        });
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          (old) => {
            if (!old) return undefined;
            return {
              ...old,
              recording_status: RecordingStatus.IDLE,
              streamingStatus: StreamingStatus.UPLOADING,
            };
          }
        );

        // --- Phase 1: Stop session (device + upload pipeline + sync_tracking cleanup) ---
        // Captured once so the raise and the clear cannot disagree if the
        // device drops while the stop is still draining.
        const isChunkLiveStop = jotaiStore.get(chronicleSupportsChunkLiveAtom);
        const releaseStopSuppression = () =>
          jotaiStore.set(chronicleChunkLiveStoppingSessionsAtom, (prev) => {
            if (!prev.has(sessionId)) return prev;
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
          });
        let suppressionCeiling: ReturnType<typeof setTimeout> | undefined;
        if (isChunkLiveStop) {
          jotaiStore.set(chronicleChunkLiveStoppingSessionsAtom, (prev) => {
            const next = new Set(prev);
            next.add(sessionId);
            return next;
          });
          suppressionCeiling = setTimeout(() => {
            logger.warn(
              '[useChronicleStopRecording] chunk-live stop exceeded the suppression ceiling — releasing the sync widget',
              { sessionId }
            );
            releaseStopSuppression();
          }, CHUNK_LIVE_STOP_SUPPRESSION_CEILING_MS);
        }
        try {
          const userId = getCurrentUser()?.user_ref_id;
          // BUG B: a live-recovery end (the reconnected-pending-end
          // window) is a CLEAN stop (isInterrupted stays false — no "Session
          // Interrupted" UI, no pending_session hazard) but its ONLY complete
          // copy is the on-device file: the recovered recording records
          // on-device and never streamed live into the online pipeline. Ask Rust
          // to KEEP the device file + sync_tracking row so bulk-sync recovers the
          // full recording (→ /hardware-audio-transcription overrides the partial
          // live transcription). Read fresh from the jotai store here, BEFORE the
          // atom is cleared in Phase 3 below. Flag-gated: OFF ⇒ always false ⇒
          // byte-for-byte unchanged. Covers BOTH stop entry points (device-stop
          // detector and manual "End transcribing").
          preserveDeviceFileForRecovery =
            isRemoteSyncAsyncEnabled &&
            jotaiStore.get(chronicleReconnectedPendingEndAtom) === sessionId;
          // (iOS parity): the online-segment note generated during
          // the drop is already COMPLETED but STALE — the final note only
          // exists after bulk-sync uploads the preserved device file and the
          // server regenerates from it. Mark the session as awaiting its
          // final note so NoteTabPane masks the stale note with the note-gen
          // loading canvas; useChronicleBulkSyncEvents clears the mark on the
          // recovered task's session_engine:upload_complete.
          if (preserveDeviceFileForRecovery) {
            setAwaitingFinalNote({ sessionId });
          }
          const stopRequestedAt = Date.now();
          uploadResult = await chronicleSessionBridge.stopSession({
            isInterrupted,
            preserveDeviceFileForRecovery,
            recoverOnDirtyStop: isDirtyStopRecoveryEnabled,
            userId: userId ?? undefined,
            sessionId,
          });

          // Rust kept the device file for the bulk sweep — mask the stale online
          // note (same signal as preserveDeviceFileForRecovery above).
          if (uploadResult.recoveryPending) {
            setAwaitingFinalNote({ sessionId });
          }

          logger.debug('[useChronicleStopRecording] Upload pipeline flushed', {
            sessionId,
            ...uploadResult,
          });
          // APP-6013: prefer the cumulative session timer (initialised in
          // useChronicleStartRecording.ts:322 with
          // `existingDuration + elapsedSinceStartedAt`) over
          // `uploadResult.durationMs`, which only carries this recording's
          // Rust-side wall-clock. The timer value already accounts for prior
          // recordings on the same session; using `uploadResult.durationMs`
          // here under-reports total session duration whenever the user
          // resumes recording on the same session.
          const durationSeconds = store.getState().timerModel.seconds;
          // One HardwareRecordingStop per physical stop.
          const stopReason = resolveHardwareStopReason({
            endReason: jotaiStore.get(chronicleRecordingEndReasonAtom),
            lastStopReason: jotaiStore.get(chronicleDeviceStateAtom).lastStopReason,
            isInterrupted,
          });
          trackHardwareRecordingStop({
            sessionId,
            sessionIdLocal: sessionId,
            origin: args.origin,
            durationSeconds,
            stopReason,
          });

          trackRemoteRecordingStopped({
            sessionId,
            sessionIdLocal: sessionId,
            pipeline: mapRouteToPipeline(isChunkLiveStop ? 'chunk_pull' : 'stream'),
            recordingDurationSeconds: durationSeconds,
            waitAfterStopSeconds: (Date.now() - stopRequestedAt) / 1000,
            chunksTotal: isChunkLiveStop ? uploadResult.totalChunks : null,
            outcome: mapStopOutcomeForDeviceEvent({
              recoveryPending: uploadResult.recoveryPending,
              totalChunks: uploadResult.totalChunks,
              isChunkLive: isChunkLiveStop,
            }),
          });
        } catch (error) {
          stopFlushError = error;
          resetArtifactGeneration({ kind: 'note', sessionId });
          logger.error('[useChronicleStopRecording] Stop/flush error', {
            sessionId,
            error: safeErrorFields(error),
          });
          trackHardwareRecordingStopFailed({
            sessionIdLocal: sessionId,
            origin: args.origin,
            errorType: error instanceof Error ? error.name : 'UNKNOWN',
            errorDescription: error instanceof Error ? error.message : String(error),
          });

          toast({
            tone: 'destructive',
            title: t('settings.chronicle.notification.stopRecordingErrorTitle'),
            description:
              error instanceof Error
                ? error.message
                : t('settings.chronicle.notification.stopRecordingErrorDescription'),
          });
        } finally {
          if (isChunkLiveStop) {
            clearTimeout(suppressionCeiling);
            releaseStopSuppression();
          }
        }

        // recording_stop ends here, at the device stop and flush: template
        // checks, note generation and cleanup below are not the attempt. Only
        // a desktop click opens one; a device-only stop has no intent or entry.
        // `currentSeconds` is this session's pause-aware value captured before
        // the shared timer singleton can move to another session.
        const cachedDurationS = queryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId },
        ])?.duration;
        const recordingSeconds = Math.round(
          Math.max(
            Number(currentSeconds) || 0,
            typeof cachedDurationS === 'number' ? cachedDurationS : 0
          )
        );
        const metadata =
          recordingSeconds > 0 ? { recordingDurationS: recordingSeconds } : undefined;
        // An interrupted stop deliberately does not command the device, so
        // capture is not confirmed stopped even when the flush succeeds.
        let stopAttemptOutcome: ChronicleStopAttemptOutcome;
        if (isInterrupted) {
          stopAttemptOutcome = {
            failed: true,
            error: new DesktopCaptureError(
              DesktopCaptureFailure.REMOTE_DEVICE_DISCONNECTED,
              'Heidi Remote disconnected; the device was not commanded to stop'
            ),
            metadata,
          };
        } else if (uploadResult === null) {
          stopAttemptOutcome = { failed: true, error: stopFlushError, metadata };
        } else {
          stopAttemptOutcome = { failed: false, metadata };
        }
        capturedStopOutcomes.set(sessionId, stopAttemptOutcome);
        finishStopAttempt(pendingStopAttempt.get(sessionId), stopAttemptOutcome);
        // Closed: a later throw must not report this attempt again.
        pendingStopAttempt.delete(sessionId);

        // Ensure template is selected (APP-2491)
        try {
          await ensureTemplateSelected(sessionId);
        } catch (error) {
          logger.error('[useChronicleStopRecording] ensureTemplateSelected error', {
            sessionId,
            error: safeErrorFields(error),
          });
        }

        // --- Phase 2: Post-recording note generation (always runs) ---
        // Runs even if pipeline flush failed — the backend may still have
        // enough partial audio (pre-flush chunks + backup) to generate a note.
        // Mirrors iOS: canGenerateNote checks backupUploadState OR recordingState.
        //
        // _streamResponse handles the full lifecycle:
        // streamingStatus UPLOADING → AWAITING_STREAMING_START → STREAMING → done
        // It calls waitForChunksToFinishUploading which resolves immediately
        // for Chronicle (tauriRecordModel.chunks.blobs is empty → vacuous truth).
        // By this point, chronicle_session_stop() has already ensured all
        // audio is on the server (blocks until all uploads + :finish complete).
        try {
          triggerPostRecordingGeneration({
            sessionId,
            sessionData,
            template,
            createEmptyDocumentFromNote,
            dispatch,
          });
        } catch (error) {
          completeArtifactGeneration({ kind: 'note', sessionId });
          logger.error('[useChronicleStopRecording] Post-recording generation error', {
            sessionId,
            error: safeErrorFields(error),
          });
        }

        // --- Phase 3: Cleanup (always runs) ---
        // 3. Persist the cumulative session duration tracked by the timer
        //    model. `currentSeconds` (captured at line 214) was initialised
        //    in `useChronicleStartRecording.ts:322` with
        //    `existingDuration + elapsedSinceStartedAt`, so it already
        //    accumulates across prior recordings on the same session.
        //
        //    Previously this branch used `uploadResult.durationMs` from
        //    Rust (`online_session.rs:1410`), which is just *this*
        //    recording's `started_at.elapsed()` wall-clock. On a session
        //    with multiple recordings (e.g. start → stop → start → stop),
        //    that PATCH would overwrite the accumulated total with the
        //    latest recording's value — making the displayed session
        //    duration appear to shrink after every resume (APP-6013).
        //
        //    Timer was already set to `currentSeconds` at line 215 via
        //    `onStopWithDuration(currentSeconds)` — no further timer
        //    correction is needed here.
        const durationSeconds = currentSeconds > 0 ? currentSeconds : null;
        const shouldPersistDuration = durationSeconds !== null;

        if (shouldPersistDuration) {
          // The periodic in-recording PATCH in sessionHeader only fires on
          // `seconds % 60 === 0`, so the cache otherwise keeps the floored value.
          queryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId }],
            (old) => (old ? { ...old, duration: durationSeconds } : old)
          );
        }

        // 4. recording_status already set to IDLE optimistically above.
        //    Re-apply to ensure consistency (idempotent).

        // 5. PATCH backend recording_status → IDLE (and authoritative duration).
        //    For desktop-initiated stops, useRecordingButton already calls
        //    updateRecordingStatus.mutate({ IDLE }) before this hook runs.
        //    For device-initiated stops, no prior PATCH happens — this
        //    ensures the backend knows the session is IDLE regardless of origin.
        //    Idempotent: setting IDLE when already IDLE is a no-op.
        // 6. Session-cache invalidation chains off the PATCH so the refetch
        //    can't return the stale floored duration before the server commits.
        void updateSessionById(sessionId, {
          recording_status: RecordingStatus.IDLE,
          ...(shouldPersistDuration && { duration: durationSeconds }),
        })
          .catch((err: unknown) => {
            logger.warn('[useChronicleStopRecording] Failed to PATCH session', {
              sessionId,
              error: safeErrorFields(err),
            });
          })
          .finally(() => {
            void queryClient.invalidateQueries({
              queryKey: [GET_SESSION_BY_ID, { sessionId }],
            });
          });

        void queryClient.invalidateQueries({
          queryKey: [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
        });

        // 7. Refresh the interrupted set from Rust before step 8 drops the
        //    active-session exclusion — a refetch during the recording can have
        //    captured this session while its sync_tracking row was still
        //    `session_created`, and revealing that snapshot shows a false
        //    "Session Interrupted" on a clean stop. Read directly (not via
        //    invalidation, which resolves even when its refetch fails or is
        //    paused) so the cache holds the post-stop row truth for every
        //    outcome, including the ones the stop result can't express.
        const stopUserId = getCurrentUser()?.user_ref_id;
        if (stopUserId) {
          let refreshTimer: ReturnType<typeof setTimeout> | undefined;
          try {
            const refresh = refreshInterruptedSessionsFromRust(stopUserId);
            // The race abandons this promise on timeout — swallow its eventual
            // rejection so it can't surface as unhandled.
            refresh.catch(() => undefined);
            await Promise.race([
              refresh,
              new Promise<never>((_, reject) => {
                refreshTimer = setTimeout(
                  () => reject(new Error('interrupted-set refresh timed out')),
                  INTERRUPTED_SET_REFRESH_TIMEOUT_MS
                );
              }),
            ]);
          } catch (error) {
            logger.warn(
              '[useChronicleStopRecording] Interrupted-set refresh failed, falling back to the derived stop outcome',
              { sessionId, error: safeErrorFields(error) }
            );
            // Correct for every stop outcome except an unprovable device-file
            // delete (row restored despite a clean result); the invalidation
            // reconciles that edge on its next successful refetch.
            setInterruptedSessionCacheMembership({
              userId: stopUserId,
              sessionId,
              interrupted:
                uploadResult == null ||
                isInterrupted === true ||
                preserveDeviceFileForRecovery ||
                uploadResult.recoveryPending,
            });
            invalidateInterruptedSessions().catch(() => undefined);
          } finally {
            clearTimeout(refreshTimer);
          }
        }

        // 8. Clear Chronicle session atoms — only while this stop's session
        //    still owns them, OR while nobody does AND no other transport is
        //    live. A device-initiated start can arm a NEWER session before this
        //    stop completes; nulling then would clobber the new recording's
        //    canonical atoms. The null-owner case (a failed/duplicate start
        //    rolled the id back mid-drain) usually has no newer owner to
        //    protect, so skipping the clears there would just strand
        //    `activeRecordingTransport` / `backgroundSession`. Same reason the
        //    nav-guard atom was already cleared at stop start. But a desktop-mic
        //    recording stamps `activeRecordingTransport` WITHOUT ever claiming
        //    `chronicleSessionIdAtom`, so a null owner is not proof the field is
        //    free — it also covers "a mic capture went live while this stop
        //    drained", whose transport marker `useRecording` routes stop/pause by.
        const atomOwner = jotaiStore.get(chronicleSessionIdAtom);
        const micOwnsTransport =
          jotaiStore.get(activeRecordingTransportAtom) === 'desktop-mic';
        const stopOwnsCleanup =
          atomOwner === sessionId || (atomOwner === null && !micOwnsTransport);
        if (stopOwnsCleanup) {
          setChronicleSessionId(null);
          setRecordingOrigin(null);
          setActiveRecordingTransport(null);
          setBackgroundSession(null);
        }

        // 8b. The reconnect window is session-keyed, so it is released on its
        //     OWN identity rather than on `chronicleSessionIdAtom` ownership.
        //     Gating it on (8) would leave a newer session inheriting a window
        //     it never opened — the RECONNECTED card, the transcribing overlay,
        //     the device-stop suppression and `processRecordingDrop`'s
        //     reconnect branch all read it.
        if (jotaiStore.get(chronicleReconnectedPendingEndAtom) === sessionId) {
          setReconnectedPendingEnd(null);
        }

        // 9. Restart desktop idle stream (release was done on start) — unless
        //    a newer recording already owns the session atom, or a desktop-mic
        //    capture owns the transport: the lifecycle listener is global (no
        //    sessionId filter), so this stop's stale stopped event would restart
        //    the idle mic stream underneath the live capture. Keyed on the same
        //    ownership as the clears above, NOT on `timerModel.isRunning`:
        //    `Timer.pause()` clears `isRunning`, so a newer armed-but-paused
        //    recording would otherwise let the stale event through — exactly
        //    the case this guard exists to prevent.
        if (stopOwnsCleanup) {
          void recordingLifecycle.emitStopped(sessionId);
        }

        // 10. Track analytics
        const seconds = store.getState().timerModel.seconds;
        sessionStopRecording({
          sessionId,
          duration: seconds,
        });

        logger.debug('[useChronicleStopRecording] Recording stopped', {
          sessionId,
          origin: args.origin,
          durationMs: uploadResult?.durationMs ?? null,
        });
        return 'stopped';
      } catch (error) {
        // Still pending only when the throw came before the device stop and flush.
        finishStopAttempt(pendingStopAttempt.get(sessionId), {
          failed: true,
          error,
        });
        const noteKey = { kind: 'note' as const, sessionId };
        if (getArtifactGenerationSnapshot(noteKey).value === 'uploading') {
          completeArtifactGeneration(noteKey);
        }
        throw error;
      } finally {
        stoppingSessionIds.delete(sessionId);
        pendingStopAttempt.delete(sessionId);
        capturedStopOutcomes.delete(sessionId);
      }
    },
    [
      chronicleSessionId,
      createEmptyDocumentFromNote,
      dispatch,
      ensureTemplateSelected,
      isDirtyStopRecoveryEnabled,
      isRemoteSyncAsyncEnabled,
      jotaiStore,
      queryClient,
      sessionData,
      setAwaitingFinalNote,
      setChronicleSessionId,
      setRecordingOrigin,
      setActiveRecordingTransport,
      setActiveSessionId,
      setBackgroundSession,
      setReconnectedPendingEnd,
      setLiveOffline,
      t,
      template,
    ]
  );

  return {
    stopChronicleRecording,
    isStopping: chronicleSessionId != null && stoppingSessionIds.has(chronicleSessionId),
  };
}
